import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import FolderPicker from "@/components/FolderPicker";
import GooglePickerButton from "@/components/GooglePickerButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { ArrowLeft, ArrowRight, Save, Sparkles, FolderTree, Calendar, Filter, Settings, Plug, Database } from "lucide-react";
import { toast } from "sonner";
import ProviderIcon from "@/components/ProviderIcon";

const DEFAULT_JOB = {
  name: "",
  source_account_id: "",
  source_folder_id: "root",
  source_folder_name: "",
  dest_type: "drive", // "drive" or "s3"
  dest_account_id: "",
  dest_folder_id: "root",
  dest_folder_name: "",
  dest_s3_prefix: "",
  schedule: { frequency: "daily", hour: 2, minute: 0, day_of_week: 0, day_of_month: 1, timezone: "Europe/Paris" },
  filters: { include_extensions: [], exclude_extensions: [], max_size_mb: null, exclude_folder_names: [] },
  incremental: true,
  versioning: false,
  enabled: true,
};

const STEPS = ["step1", "step2", "step3", "step4", "step5"];

export default function JobWizard() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [accounts, setAccounts] = useState(null);
  const [s3Accounts, setS3Accounts] = useState([]);
  const [step, setStep] = useState(0);
  const [job, setJob] = useState(DEFAULT_JOB);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/drive/accounts").then(({ data }) => setAccounts(data));
    api.get("/s3/accounts").then(({ data }) => setS3Accounts(data || [])).catch(() => setS3Accounts([]));
    if (isEdit) {
      api.get(`/jobs/${id}`).then(({ data }) => setJob({ ...DEFAULT_JOB, ...data }));
    }
  }, [id, isEdit]);

  const set = (patch) => setJob((j) => ({ ...j, ...patch }));
  const setSchedule = (patch) => setJob((j) => ({ ...j, schedule: { ...j.schedule, ...patch } }));
  const setFilters = (patch) => setJob((j) => ({ ...j, filters: { ...j.filters, ...patch } }));

  const canNext = useMemo(() => {
    if (step === 0) return job.name.trim().length > 0;
    if (step === 1) return !!job.source_account_id;
    if (step === 2) return !!job.dest_account_id;
    return true;
  }, [step, job]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...job,
        // ensure folder_name is set
        source_folder_name: job.source_folder_name || t("common.root"),
        dest_folder_name: job.dest_folder_name || t("common.root"),
      };
      if (isEdit) {
        await api.patch(`/jobs/${id}`, payload);
        toast.success(t("common.save"));
      } else {
        const { data } = await api.post("/jobs", payload);
        toast.success(t("common.create"));
        navigate(`/jobs/${data.id}`);
        return;
      }
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="App min-h-screen bg-background">
      <Header variant="app" />
      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-1 font-mono text-xs text-muted-foreground hover:text-foreground"
          data-testid="wizard-back"
        >
          <ArrowLeft className="h-3 w-3" /> {t("common.back")}
        </button>
        <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// {isEdit ? "Edit job" : "New job"}</div>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
          {isEdit ? t("wizard.editTitle") : t("wizard.title")}
        </h1>

        {/* Loading state */}
        {!isEdit && accounts === null && (
          <div className="mt-8 font-mono text-sm text-muted-foreground">{t("common.loading")}</div>
        )}

        {/* Guard: need at least 2 connected accounts before creating a job */}
        {!isEdit && accounts !== null && accounts.length < 2 && (
          <div
            className="mt-8 border border-[#002FA7]/40 bg-[#002FA7]/5 p-6"
            data-testid="wizard-no-accounts-guard"
          >
            <div className="flex items-center gap-2 font-mono text-xs uppercase text-[#002FA7]">
              <Plug className="h-4 w-4" /> {t("wizard.guardTitle")}
            </div>
            <h2 className="mt-3 font-display text-xl font-bold tracking-tight">
              {accounts.length === 0 ? t("wizard.guardNoneHeading") : t("wizard.guardOneHeading")}
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              {accounts.length === 0 ? t("wizard.guardNoneBody") : t("wizard.guardOneBody")}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button
                onClick={() => navigate("/accounts")}
                className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2"
                data-testid="wizard-goto-accounts"
              >
                <Plug className="h-4 w-4" /> {t("wizard.guardCta")}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="rounded-sm gap-2"
                data-testid="wizard-back-to-dashboard"
              >
                <ArrowLeft className="h-4 w-4" /> {t("wizard.guardBack")}
              </Button>
            </div>
            {accounts.length === 1 && (
              <div className="mt-5 flex items-center gap-3 border-t border-border pt-4 font-mono text-[11px] text-muted-foreground">
                <span className="text-foreground">1/2</span>
                <span>·</span>
                <span>{accounts[0].google_email}</span>
              </div>
            )}
          </div>
        )}

        {/* Stepper */}
        {(isEdit || (accounts !== null && accounts.length >= 2)) && (
        <>
        <div className="mt-8 grid grid-cols-5 gap-1">
          {STEPS.map((s, i) => (
            <button
              key={s}
              type="button"
              onClick={() => setStep(i)}
              className={`group border-b-2 pb-2 text-left transition ${
                i === step ? "border-[#002FA7]" : i < step ? "border-foreground/40" : "border-border"
              }`}
              data-testid={`wizard-step-${i}`}
            >
              <div className="font-mono text-[10px] uppercase text-muted-foreground">0{i + 1}</div>
              <div className={`font-display text-sm font-bold tracking-tight ${i === step ? "text-foreground" : "text-muted-foreground"}`}>
                {t(`wizard.${s}`)}
              </div>
            </button>
          ))}
        </div>

        {/* Step content */}
        <div className="mt-8 border border-border bg-card p-6">
          {step === 0 && (
            <div className="space-y-4">
              <SectionHeader icon={Sparkles} label="01 — Identity" />
              <div>
                <Label htmlFor="name" className="font-mono text-xs uppercase text-muted-foreground">
                  {t("wizard.jobName")}
                </Label>
                <Input
                  id="name"
                  className="mt-2 rounded-sm h-11"
                  value={job.name}
                  placeholder={t("wizard.jobNamePlaceholder")}
                  onChange={(e) => set({ name: e.target.value })}
                  data-testid="wizard-job-name"
                />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <SectionHeader icon={FolderTree} label="02 — Source (Drive A)" />
              <AccountSelector
                value={job.source_account_id}
                onChange={(v) => set({ source_account_id: v, source_folder_id: "root", source_folder_name: t("common.root") })}
                accounts={accounts}
                testId="source-account-select"
              />
              <div>
                <Label className="font-mono text-xs uppercase text-muted-foreground">{t("wizard.pickFolder")}</Label>
                <div className="mt-2">
                  <SmartPicker
                    accountId={job.source_account_id}
                    accounts={accounts}
                    value={{ id: job.source_folder_id, name: job.source_folder_name }}
                    onChange={(f) => set({ source_folder_id: f.id, source_folder_name: f.name })}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <SectionHeader icon={FolderTree} label={t("wizard.destStepLabel")} />

              {/* Destination type toggle */}
              <div>
                <Label className="font-mono text-xs uppercase text-muted-foreground">
                  {t("wizard.destType")}
                </Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="dest-type-drive"
                    onClick={() => set({
                      dest_type: "drive",
                      dest_account_id: "",
                      dest_folder_id: "root",
                      dest_folder_name: t("common.root"),
                      dest_s3_prefix: "",
                    })}
                    className={`flex items-center gap-3 border p-3 text-left transition-colors ${
                      job.dest_type === "drive"
                        ? "border-foreground bg-card"
                        : "border-border bg-background hover:bg-card"
                    }`}
                  >
                    <FolderTree className="h-5 w-5 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{t("wizard.destTypeDrive")}</div>
                      <div className="text-[11px] text-muted-foreground">{t("wizard.destTypeDriveHint")}</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    data-testid="dest-type-s3"
                    onClick={() => set({
                      dest_type: "s3",
                      dest_account_id: "",
                      dest_folder_id: "root",
                      dest_folder_name: "",
                      dest_s3_prefix: "",
                    })}
                    className={`flex items-center gap-3 border p-3 text-left transition-colors ${
                      job.dest_type === "s3"
                        ? "border-foreground bg-card"
                        : "border-border bg-background hover:bg-card"
                    }`}
                  >
                    <Database className="h-5 w-5 shrink-0 text-[#FF3D00]" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{t("wizard.destTypeS3")}</div>
                      <div className="text-[11px] text-muted-foreground">{t("wizard.destTypeS3Hint")}</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* DRIVE destination */}
              {job.dest_type === "drive" && (
                <>
                  <AccountSelector
                    value={job.dest_account_id}
                    onChange={(v) => set({ dest_account_id: v, dest_folder_id: "root", dest_folder_name: t("common.root") })}
                    accounts={accounts}
                    testId="dest-account-select"
                  />
                  <div>
                    <Label className="font-mono text-xs uppercase text-muted-foreground">{t("wizard.pickFolder")}</Label>
                    <div className="mt-2">
                      <SmartPicker
                        accountId={job.dest_account_id}
                        accounts={accounts}
                        value={{ id: job.dest_folder_id, name: job.dest_folder_name }}
                        onChange={(f) => set({ dest_folder_id: f.id, dest_folder_name: f.name })}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* S3 destination */}
              {job.dest_type === "s3" && (
                <>
                  {s3Accounts.length === 0 ? (
                    <div className="border border-dashed border-border bg-card p-5 text-sm">
                      <div className="mb-2 font-medium">{t("wizard.noS3Accounts")}</div>
                      <div className="text-muted-foreground">{t("wizard.noS3AccountsHint")}</div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-3 rounded-sm"
                        onClick={() => navigate("/accounts?tab=s3")}
                        data-testid="goto-s3-accounts"
                      >
                        {t("wizard.addS3Account")}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div>
                        <Label className="font-mono text-xs uppercase text-muted-foreground">{t("wizard.s3Bucket")}</Label>
                        <Select
                          value={job.dest_account_id}
                          onValueChange={(v) => set({ dest_account_id: v })}
                        >
                          <SelectTrigger className="mt-2 rounded-sm h-11" data-testid="s3-account-select">
                            <SelectValue placeholder={t("wizard.s3BucketPick")} />
                          </SelectTrigger>
                          <SelectContent className="rounded-sm">
                            {s3Accounts.map((a) => (
                              <SelectItem key={a.id} value={a.id}>
                                {a.name} · {a.bucket} ({a.region})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase text-muted-foreground">
                          {t("wizard.s3Prefix")}
                        </Label>
                        <Input
                          type="text"
                          value={job.dest_s3_prefix}
                          onChange={(e) => set({ dest_s3_prefix: e.target.value })}
                          placeholder={t("wizard.s3PrefixPlaceholder")}
                          className="mt-2 rounded-sm h-11"
                          data-testid="s3-prefix-input"
                        />
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          {t("wizard.s3PrefixHint")}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <SectionHeader icon={Calendar} label="04 — Schedule" />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label className="font-mono text-xs uppercase text-muted-foreground">{t("wizard.frequency")}</Label>
                  <Select value={job.schedule.frequency} onValueChange={(v) => setSchedule({ frequency: v })}>
                    <SelectTrigger className="mt-2 rounded-sm h-11" data-testid="schedule-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-sm">
                      <SelectItem value="manual">{t("wizard.frequencyManual")}</SelectItem>
                      <SelectItem value="hourly">{t("wizard.frequencyHourly")}</SelectItem>
                      <SelectItem value="daily">{t("wizard.frequencyDaily")}</SelectItem>
                      <SelectItem value="weekly">{t("wizard.frequencyWeekly")}</SelectItem>
                      <SelectItem value="monthly">{t("wizard.frequencyMonthly")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {job.schedule.frequency !== "manual" && job.schedule.frequency !== "hourly" && (
                  <div>
                    <Label className="font-mono text-xs uppercase text-muted-foreground">{t("wizard.time")}</Label>
                    <div className="mt-2 flex gap-2">
                      <Input
                        type="number"
                        min={0}
                        max={23}
                        className="rounded-sm h-11"
                        value={job.schedule.hour}
                        onChange={(e) => setSchedule({ hour: Number(e.target.value) })}
                        data-testid="schedule-hour"
                      />
                      <span className="self-center font-mono">:</span>
                      <Input
                        type="number"
                        min={0}
                        max={59}
                        className="rounded-sm h-11"
                        value={job.schedule.minute}
                        onChange={(e) => setSchedule({ minute: Number(e.target.value) })}
                        data-testid="schedule-minute"
                      />
                    </div>
                  </div>
                )}

                {job.schedule.frequency === "hourly" && (
                  <div>
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Minute</Label>
                    <Input
                      type="number"
                      min={0}
                      max={59}
                      className="mt-2 rounded-sm h-11"
                      value={job.schedule.minute}
                      onChange={(e) => setSchedule({ minute: Number(e.target.value) })}
                      data-testid="schedule-minute-hourly"
                    />
                  </div>
                )}

                {job.schedule.frequency === "weekly" && (
                  <div>
                    <Label className="font-mono text-xs uppercase text-muted-foreground">{t("wizard.dayOfWeek")}</Label>
                    <Select
                      value={String(job.schedule.day_of_week)}
                      onValueChange={(v) => setSchedule({ day_of_week: Number(v) })}
                    >
                      <SelectTrigger className="mt-2 rounded-sm h-11" data-testid="schedule-dow">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-sm">
                        {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((d, i) => (
                          <SelectItem key={d} value={String(i)}>
                            {t(`wizard.${d}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {job.schedule.frequency === "monthly" && (
                  <div>
                    <Label className="font-mono text-xs uppercase text-muted-foreground">{t("wizard.dayOfMonth")}</Label>
                    <Input
                      type="number"
                      min={1}
                      max={28}
                      className="mt-2 rounded-sm h-11"
                      value={job.schedule.day_of_month}
                      onChange={(e) => setSchedule({ day_of_month: Number(e.target.value) })}
                      data-testid="schedule-dom"
                    />
                  </div>
                )}

                <div>
                  <Label className="font-mono text-xs uppercase text-muted-foreground">{t("wizard.timezone")}</Label>
                  <Select value={job.schedule.timezone} onValueChange={(v) => setSchedule({ timezone: v })}>
                    <SelectTrigger className="mt-2 rounded-sm h-11" data-testid="schedule-tz">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-sm">
                      {["UTC", "Europe/Paris", "Europe/London", "America/New_York", "America/Los_Angeles", "Asia/Tokyo", "Australia/Sydney"].map((tz) => (
                        <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <SectionHeader icon={Filter} label="05 — Filters & options" />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label className="font-mono text-xs uppercase text-muted-foreground">{t("wizard.includeExtensions")}</Label>
                  <Input
                    className="mt-2 rounded-sm h-11"
                    placeholder="pdf, docx, jpg"
                    value={(job.filters.include_extensions || []).join(", ")}
                    onChange={(e) => setFilters({ include_extensions: parseList(e.target.value) })}
                    data-testid="filter-include-ext"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase text-muted-foreground">{t("wizard.excludeExtensions")}</Label>
                  <Input
                    className="mt-2 rounded-sm h-11"
                    placeholder="tmp, log"
                    value={(job.filters.exclude_extensions || []).join(", ")}
                    onChange={(e) => setFilters({ exclude_extensions: parseList(e.target.value) })}
                    data-testid="filter-exclude-ext"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase text-muted-foreground">{t("wizard.maxSize")}</Label>
                  <Input
                    type="number"
                    min={0}
                    className="mt-2 rounded-sm h-11"
                    placeholder="0 = unlimited"
                    value={job.filters.max_size_mb ?? ""}
                    onChange={(e) =>
                      setFilters({ max_size_mb: e.target.value ? Number(e.target.value) : null })
                    }
                    data-testid="filter-max-size"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase text-muted-foreground">{t("wizard.excludeFolders")}</Label>
                  <Input
                    className="mt-2 rounded-sm h-11"
                    placeholder="Trash, Cache"
                    value={(job.filters.exclude_folder_names || []).join(", ")}
                    onChange={(e) => setFilters({ exclude_folder_names: parseList(e.target.value) })}
                    data-testid="filter-exclude-folders"
                  />
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <SectionHeader icon={Settings} label="Options" />
                <OptionToggle
                  label={t("wizard.incremental")}
                  desc={t("wizard.incrementalDesc")}
                  value={job.incremental}
                  onChange={(v) => set({ incremental: v })}
                  testId="opt-incremental"
                />
                <OptionToggle
                  label={t("wizard.versioning")}
                  desc={t("wizard.versioningDesc")}
                  value={job.versioning}
                  onChange={(v) => set({ versioning: v })}
                  testId="opt-versioning"
                />
                <OptionToggle
                  label={t("wizard.enabled")}
                  desc=""
                  value={job.enabled}
                  onChange={(v) => set({ enabled: v })}
                  testId="opt-enabled"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="outline"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="rounded-sm gap-2"
            data-testid="wizard-prev"
          >
            <ArrowLeft className="h-4 w-4" /> {t("common.previous")}
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              disabled={!canNext}
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2"
              data-testid="wizard-next"
            >
              {t("common.next")} <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              disabled={saving || !canNext}
              onClick={save}
              className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2"
              data-testid="wizard-save"
            >
              <Save className="h-4 w-4" /> {isEdit ? t("wizard.update") : t("wizard.create")}
            </Button>
          )}
        </div>
        </>
        )}
      </main>
    </div>
  );
}

function parseList(s) {
  return s
    .split(",")
    .map((x) => x.trim().toLowerCase().replace(/^\./, ""))
    .filter(Boolean);
}

function SmartPicker({ accountId, accounts, value, onChange }) {
  // Use the official Google Picker for Google accounts (required by drive.file scope).
  // For OneDrive / Dropbox / other providers, keep the custom folder browser.
  const acct = accounts?.find((a) => a.id === accountId);
  if (acct?.provider === "google") {
    return <GooglePickerButton accountId={accountId} value={value} onChange={onChange} />;
  }
  return <FolderPicker accountId={accountId} value={value} onChange={onChange} />;
}


function SectionHeader({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 border-b border-border pb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
  );
}

function AccountSelector({ value, onChange, accounts, testId }) {
  const { t } = useI18n();
  const list = accounts || [];
  return (
    <div>
      <Label className="font-mono text-xs uppercase text-muted-foreground">{t("wizard.pickAccount")}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-2 rounded-sm h-11" data-testid={testId}>
          <SelectValue placeholder={t("wizard.pickAccount")} />
        </SelectTrigger>
        <SelectContent className="rounded-sm">
          {list.map((a) => (
            <SelectItem key={a.id} value={a.id} data-testid={`${testId}-opt-${a.id}`}>
              <span className="flex items-center gap-2">
                <ProviderIcon provider={a.provider} className="h-3.5 w-3.5" />
                <span>{a.google_name || a.google_email} — <span className="text-muted-foreground">{a.google_email}</span></span>
              </span>
            </SelectItem>
          ))}
          {list.length === 0 && (
            <div className="p-3 text-xs text-muted-foreground font-mono">No accounts. Connect one first.</div>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

function OptionToggle({ label, desc, value, onChange, testId }) {
  return (
    <div className="mt-4 flex items-center justify-between gap-4">
      <div>
        <div className="font-display text-sm font-bold">{label}</div>
        {desc && <div className="text-xs text-muted-foreground">{desc}</div>}
      </div>
      <Switch checked={value} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}
